// ============================================================================
// ui/pick-weights-panel.js — the "Pick Weights" TAB (in-tool), rebuilt for the pane
// ============================================================================
// The gate's `select-weights-step.js` renders four SQUARE cards sized for the wide
// intro stage. Reused verbatim as the in-tool tab it collapsed into a row of tiny
// overlapping squares whose hit-targets stacked, so nothing clicked (Ryan 2026-07-16).
//
// This is the pane-native replacement: a COMPACT ACCORDION of four landscape glass
// cards (Ryan's spec, chosen over a big-image or flat-list variant). Each card header
// carries a small product thumbnail (the same art the gate cards use), the category
// name, a count badge (white number in a blue circle) of how many of its products are
// selected, and a chevron. Click a header to expand it — ONE open at a time — revealing
// that category's product pills. Selecting/deselecting a pill is LIVE: it writes the
// SAME `state.ui.selectedProducts` the gate does and fires `onChange`, which main.js
// wires to the on-canvas switcher + the live 3D (re-homing the stage if you remove the
// product you're viewing). No apply button, no scrolling on desktop.
//
// DOM is built ONCE and mutated in place (never rebuilt) — so the grid-rows expand
// animates and toggling a pill can't flash the whole list.
// ============================================================================

import { h } from './dom.js';
import { isPhone } from './mobile-launch.js';
import { isProductSelected, toggleProductSelection, hasAnySelection, applyDefaultPlateColors } from './state.js';
import { CATEGORIES, categoryIsDesignable, productSupportsMultiConfig, productKey } from '../products.js';
import { createConfigAdder, configRow } from './config-adder.js';
import { flyToSwitcher, tossAway } from './fly-to-switcher.js';
import bumperCard from './cards/bumper.webp';
import gripCard from './cards/grip.webp';
import dumbbellCard from './cards/dumbbell.webp';
import barbellCard from './cards/barbell.webp';

const CARD_ART = { bumper: bumperCard, grip: gripCard, dumbbell: dumbbellCard, barbell: barbellCard };
const CHEVRON = '<svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6l4 4 4-4"/></svg>';

/**
 * @param {object} ctx - shared app context (ctx.state, ctx.announce, …).
 * @param {{onChange?: () => void}} [opts] onChange fires after any product toggle —
 *   main.js uses it to follow the switcher + live product to the edited set.
 * @returns {{ el: HTMLElement, refresh: () => void }}
 */
export function createPickWeightsPanel(ctx, opts = {}) {
  const { onChange } = opts;
  let openCat = null; // which category is expanded (accordion — one at a time)

  const list = h('div', { class: 'wd-pw-list' });
  const el = h('div', { class: 'wd-step wd-step--pick-weights' }, list);

  /**
   * One product pill, plus its "Add another color" control on the products that have one.
   *
   * ⭐ BESIDE THE PILL, NOT ON IT (Ryan 2026-08-09: *"then we don't have buttons on top of buttons
   * which can be confusing to some folks"*). The row is a three-column grid and the pill gives up
   * the 68px the two slots cost. It comes out even: the pill was already reserving 36px of padding
   * at each end to keep its label clear of the overlaid buttons, so the label still gets its same
   * 178px. EVERY row reserves its two control columns, in every category — see the note in the
   * body for why that stopped being category-scoped.
   */
  /* ⭐ THE PANE'S OWN CONTROL, NOT THE GATE'S (Ryan 2026-08-09). This used to be a `.wd-kind-chip` —
     the gate's light `#E6EBF2` pill with the blue wipe, built for the bright intro stage and reused
     verbatim inside a dark `.wd-flat-glass` accordion card. It was the one place in the pane where
     "this one is chosen" was said by a pill filling in rather than by the pane's own vocabulary.
       It is now `.wd-lockup` + flat glass — the SAME recipe as the Size ladder rung one tab over,
     which is very nearly the same decision (which of these is in my order) in the same pane. That
     brings `--wd-lockup-h`, the hover wash and, on `is-active`, `--wd-ring-selected`, the ring the
     CSS notes is already worn by eight controls. `--flush` because these sit ON another glass
     surface, which is exactly why the Size tiles carry it too.
       ⚠ Reused, not copied (memory `match-shared-ui-by-sharing-rules`): everything above comes from
     `.wd-lockup`'s existing rules, and `.wd-pw-pill` adds only what a full-width ROW needs that a
     grid tile does not. */
  function pillFor(cat, kind) {
    const label = h('span', { class: 'wd-pw-pill__t' }, kind);
    const chip = h('button', {
      type: 'button',
      class: 'wd-pw-pill wd-lockup wd-flat-glass wd-flat-glass--flush',
      'aria-pressed': 'false',
      'data-kind': kind,
      onClick: () => togglePill(cat, kind),
    }, label);
    /* ⛔ EVERY ROW RESERVES, IN EVERY CATEGORY (Ryan 2026-08-12) — this used to be category-scoped.
     *
     * The old rule: *"a category with no dyeable product at all keeps a bare pill at full width —
     * reserving two empty columns in the Grip or Barbell card would narrow every row there to make
     * room for a control that can never appear in it."* Locally sound, and it made a card whose
     * products differ line up (the Training Rubber under two Competition rows, 2026-08-09). What it
     * did NOT do is make the CARDS agree: a Grip or Barbell pill ran the two slots' 68px wider than
     * a bumper pill, so scrolling the accordion stepped the pill width in and out.
     *   That is the same complaint the 08-09 fix answered, one level up — *"the pills in one open
     * card come out at two different widths… which reads as a layout bug rather than as an absent
     * control"* (see `configRow`). The buyer reads the pane as one list, not as five independent
     * cards, so the comparison that matters is between rows anywhere.
     *
     * ⚠ THE COST IS REAL AND WAS MEASURED, NOT ASSUMED: every bare pill gives up 68px of label room.
     * See `tools/measure-pick-weights-labels.mjs` — the longest name in the pane still clears its
     * column with room to spare, on desktop and at the narrow breakpoint.
     *
     * ⛔ AND THE PILL DOES NOT RESIZE WHEN THE CONTROL APPEARS. Ryan asked whether it should start
     * wide and shrink to make room; it must not. `configAnchor`'s note states why — the room is
     * reserved at both ends *"so the label sits still when the count changes rather than sliding as
     * a button appears beside it"* — and a pill whose width depended on whether its control was
     * showing would defeat this uniformity in the very moment the buyer is looking at it.
     */
    if (!productSupportsMultiConfig(productKey(cat.id, kind))) return { chip, el: configRow(chip), adder: null };
    const adder = createConfigAdder(ctx, {
      category: cat.id,
      kind,
      labelEl: label,
      layout: 'beside',
      // A second colour is a second thing in the quote, so it flies too — but a removal must not.
      onChange: (why) => {
        // Same ordering rule as `togglePill`: measure the bar before the repaint moves it.
        const removedFrom = why === 'remove' ? barPoint() : null;
        afterPickChange();
        if (why === 'add') flyFrom(cat);
        else if (why === 'remove') tossFrom(cat, removedFrom);
      },
    });
    return { chip, adder, el: configRow(chip, adder.el) };
  }

  function buildCard(cat) {
    const live = categoryIsDesignable(cat);
    const badge = h('span', { class: 'wd-pw-badge', hidden: true, 'aria-label': '0 selected' }, '0');
    const header = h('button', {
      type: 'button',
      class: 'wd-pw-card wd-flat-glass',
      'aria-expanded': 'false',
      disabled: !live,
      onClick: () => toggleCard(cat.id),
    },
      h('span', { class: 'wd-pw-thumb' }, h('img', { class: 'wd-pw-img', src: CARD_ART[cat.id], alt: '', 'aria-hidden': 'true' })),
      /* ⚠ THE BADGE IS A DIRECT CHILD, not one of the right accents any more (Ryan 2026-08-09: it
         should *"barely just overlap the edge of the image"*). It cannot go INSIDE `.wd-pw-thumb`
         either — that tile is `overflow: hidden` so the art can't escape its rounded corners, and a
         badge in there would be clipped to the very edge it is meant to hang over. So it stays a
         sibling and is positioned against the header, off the same two tokens the thumb reads. */
      badge,
      h('span', { class: 'wd-pw-label' }, cat.label),
      // Right accents are absolutely positioned so the label can centre in the full card
      // width (Ryan 2026-07-16). The chevron is the only one left.
      h('span', { class: 'wd-pw-right' }, h('span', { class: 'wd-pw-chev', 'aria-hidden': 'true', html: CHEVRON })),
    );
    const units = cat.kinds.map((k) => pillFor(cat, k));
    const pills = units.map((u) => u.chip);
    const adders = units.map((u) => u.adder).filter(Boolean);
    const pillsWrap = h('div', { class: 'wd-pw-pills' }, ...units.map((u) => u.el));
    const body = h('div', { class: 'wd-pw-body', role: 'group', 'aria-label': `${cat.label} — choose products` }, pillsWrap);
    const root = h('div', { class: 'wd-pw-cat' + (live ? '' : ' is-soon'), 'data-cat-id': cat.id }, header, body);
    return { cat, live, root, header, body, pillsWrap, badge, pills, adders };
  }

  const cards = CATEGORIES.map(buildCard);
  list.append(...cards.map((c) => c.root));

  // Open/close a card's body by animating max-height to the MEASURED content height
  // (the site Q&A accordion's technique — the ease can't over/undershoot). `animate`
  // false snaps instantly (initial render + tab re-entry), true tweens (user clicks).
  function setBodyOpen(c, isOpen, animate) {
    c.header.classList.toggle('is-open', isOpen);
    c.body.classList.toggle('is-open', isOpen); // drives the pills' fade/rise + chevron
    c.header.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    // On CLOSE, snap the pills invisible instantly (kill their opacity transition) so a
    // selected pill's bright white outline can't bleed through the collapsing gap
    // (Ryan 2026-07-16). On OPEN, restore the CSS transition so they fade/rise back in.
    c.pillsWrap.style.transition = isOpen ? '' : 'none';
    const target = isOpen ? `${c.body.scrollHeight}px` : '0px';
    if (animate) {
      c.body.style.maxHeight = target;
    } else {
      const prev = c.body.style.transition;
      c.body.style.transition = 'none';
      c.body.style.maxHeight = target;
      void c.body.offsetHeight; // flush the reflow so the next change can transition
      c.body.style.transition = prev;
    }
  }

  function syncCard(c, animate) {
    /* ⚠ THE ADDERS SYNC BEFORE `setBodyOpen`, NOT AFTER, and the order is load-bearing rather than
       tidy. `setBodyOpen` animates max-height to the body's MEASURED `scrollHeight`; an adder that
       appears (or grows from "+ Add another color" to a compact "+") changes that height. Measured
       first, the open card eases to a target that is already stale and clips its own last row. */
    c.adders.forEach((a) => a.sync());
    setBodyOpen(c, c.live && openCat === c.cat.id, animate);
    let n = 0;
    c.pills.forEach((btn) => {
      const on = isProductSelected(ctx.state, c.cat.id, btn.dataset.kind);
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) n += 1;
    });
    /* ⚠ THE BADGE COUNTS CONFIGURATIONS, NOT PILLS (2026-08-09). It reads "how many of this
       category am I quoting", and two colors of one competition bumper are two blocks on the quote
       form, two pills in the switcher and two plates in the order. Counting the lit pill once would
       make the badge disagree with every downstream surface the moment the feature is used. */
    n = ctx.state.ui.selectedProducts.filter((p) => p.category === c.cat.id).length || n;
    c.header.classList.toggle('has-selection', n > 0);
    c.badge.textContent = String(n);
    c.badge.hidden = n === 0; // no badge at all for an untouched category (Ryan)
    c.badge.setAttribute('aria-label', `${n} selected`);
  }
  const syncAll = (animate) => cards.forEach((c) => syncCard(c, animate));

  // How long `.wd-pw-body`'s max-height takes (weight-designer.css). The scroll below rides the same
  // clock so the two read as one motion rather than a scroll chasing an expand.
  const ACCORDION_MS = 420;
  let scrollToken = 0;

  /**
   * Scroll the pane so a just-expanded category shows ALL of its products (Ryan 2026-07-28: "when
   * you click, the menu expands and you can only see 2.5 of the options… can you make it so that
   * when you click the button it auto-scrolls you down").
   *
   * WHY THIS IS A LOOP AND NOT `scrollIntoView`. At click time the body is still 0px — it grows over
   * the next 420ms — so the scroller's scrollable range does not YET reach the position we want. A
   * one-shot scroll (smooth or not) gets silently clamped to today's range and lands short, which is
   * exactly the half-scrolled result being complained about. Each frame re-clamps against the range
   * as it grows, so the scroll arrives exactly as the content does.
   *
   * DOWN ONLY, and never past the card's own header: if the expanded card is taller than the pane
   * there is no position that shows all of it, and pinning its top is the useful answer — you land
   * on the header you just pressed with the list running below it, rather than mid-list with the
   * header gone. A card already fully visible does nothing at all.
   */
  function revealCard(c) {
    const scroller = c.root.closest('.ds-side-panel-body');
    if (!scroller) return;
    const sr = scroller.getBoundingClientRect();
    const cr = c.root.getBoundingClientRect();
    const view = scroller.clientHeight;
    const cardTop = scroller.scrollTop + (cr.top - sr.top);
    // The height it is animating TO — `scrollHeight` is the content, unaffected by the 0px max-height
    // it currently wears. Measuring `offsetHeight` here would measure the collapsed card.
    const cardBottom = cardTop + c.header.offsetHeight + c.body.scrollHeight;
    if (cardBottom <= scroller.scrollTop + view - 2) return; // already all on screen
    const from = scroller.scrollTop;
    const want = Math.max(0, Math.min(cardTop, cardBottom - view));
    if (want - from < 2) return; // down only
    const mine = ++scrollToken;
    const t0 = performance.now();
    const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - ((-2 * x + 2) ** 3) / 2); // easeInOutCubic
    let lastSet = from;
    const step = (now) => {
      // A second toggle supersedes this one; a manual scroll wins outright (if the position moved
      // and it was not us that moved it, the buyer has taken over — stop fighting their thumb).
      if (mine !== scrollToken) return;
      if (Math.abs(scroller.scrollTop - lastSet) > 2) return;
      const t = Math.min(1, (now - t0) / ACCORDION_MS);
      const max = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      lastSet = Math.min(from + (want - from) * ease(t), max);
      scroller.scrollTop = lastSet;
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /* ── THE SHEET MUST NOT RESIZE WHEN A CATEGORY *CHANGES* (Ryan 2026-08-08, narrowed 2026-08-09) ─
   * *"when all category headers are closed, make it match the Add Logo and Customize Design tabs in
   * terms of height … that way every time you click to open/close a category, the height of the menu
   * isn't sliding up and down each time."*
   *   ⚠ AND IT IS BACK, at the cap rather than at the tallest category (2026-08-09): *"it should
   * stay locked at the max size that all the slide-up menus have."* The middle version — no reserve,
   * sheet sized to content — is gone; it killed the dead scroll but let the sheet grow on open.
   *
   * The bottom sheet is `height: auto` under a `max-height` cap (shell.css), so its height IS this
   * list's height: four shut headers measure 329px on a 390×844 phone and any open category takes it
   * to the 460px cap. Nothing here can stop that by animating better — the only fix is to RESERVE the
   * room up front, so the list never grows and the sheet has nothing to resize.
   *
   * ⚠ THE RESERVE IS THE SHEET'S CAP, NOT "THE TALLEST CATEGORY" (2026-08-09, third pass). Those are
   * different numbers and the difference is the whole bug: tallest-category measured 470 on a
   * 390×844 phone against a 460 cap, so the body carried 83px more content than viewport and
   * scrolled to a strip of nothing. Filling to the cap exactly answers both of Ryan's asks at once —
   * the sheet matches every other tab, it does not move when a category opens, and the content ends
   * where the viewport does so there is nothing to scroll to.
   *
   * ⛔ NOT A `min-height` ON THE SHEET, which is where this obviously belongs and is exactly wrong.
   * `min-height` outranks `max-height` in CSS, so the value would bind the instant `.is-open` lands
   * and the sheet would SNAP to full height instead of riding its 0.55s `max-height` slide — trading
   * a height that moves when it shouldn't for one that doesn't move when it should. Reserving on the
   * CONTENT leaves that mechanism untouched: the cap still decides the height whenever the reserve
   * exceeds it, which on a phone it almost always does.
   *
   * ⚠ THE CAP IS READ OFF THE SHEET, not recomputed. `getComputedStyle(...).maxHeight` resolves
   * shell.css's `min(58%, 100% - 240px)` to a used pixel value; restating that expression in JS
   * would be a second copy of the sheet's sizing rule, free to drift the day either term changes.
   *
   * PHONE ONLY: the desktop pane is a full-height scroller, so it has no height to hold still.
   */
  /** The last cleanly-measured fill height. See the `grow <= 0` note inside `reserveRoom`. */
  let reserved = 0;

  function reserveRoom() {
    if (!isPhone()) { list.style.minHeight = ''; reserved = 0; return; }
    list.style.minHeight = '';   // measure the natural box, never the previous reserve
    /* ⭐ ONLY WHILE SOMETHING IS OPEN (Ryan 2026-08-09, revising the rule above): *"there's all this
       scrollable gray area below the Fixed Barbells card. Don't want this. The area should only
       become visible/expanded when you click on one of the cards… But when they're closed, don't
       include a scrollable area to nowhere."*
         ⚠ THIS IS A DELIBERATE PARTIAL REVERSAL and both halves of the older ask still matter, so it
       is worth being precise about which half is gone. The reserve was doing two jobs: matching the
       other tabs' height at rest, and stopping the sheet resizing every time a category was toggled.
       The FIRST is what produced 238px of dead scroll on a 390x844 phone (four shut headers measure
       232, the reserve held 470) and it is the one being dropped. The SECOND survives intact,
       because the reserve still holds while ANY category is open — so switching from Bumpers to
       Dumbbells, which is the toggle a buyer actually repeats, still moves nothing.
         ⛔ AND THEN IT COMES OFF ENTIRELY, because the two asks turn out to conflict at the point
       where it matters. Reserving while open would make the sheet JUMP to the reserved height in one
       frame — the sheet is `height: auto` under a `max-height` cap and an auto height does not
       transition, so the smooth growth Ryan is describing ("it opens and expands DOWN") only happens
       when the list is free to follow the body's own max-height tween. Reserve and you keep the
       category-switch steady but lose the expand; don't, and you get the expand back and the sheet
       resizes when you move from Bumpers to Dumbbells.
         His current words ask for the expand, so that is what ships. The measurement below is left
       intact and correct: restoring the old behaviour is deleting the `return` on the next line and
       calling `reserveRoom()` from `toggleCard`, which is the call it never had — the original set
       the reserve once at refresh and held it forever, which is why nothing was wired to the toggle.
         ⚠ If the switching resize turns out to be the more annoying of the two, the fix is NOT to
       put this back as it was: it is to apply the reserve after the open animation settles, so the
       first expand still animates and every later switch holds still. That is more machinery than
       today's ask justifies. */
    /* ⭐ FILL THE SHEET TO ITS OWN CAP — not "the tallest category", which is what this measured
       until 2026-08-09 and is what produced the dead scroll. Ryan, third pass: *"when you click on a
       category button and it expands, the slide-up menu shouldn't change size and get bigger… it
       should stay locked at the max size that all the slide-up menus have."*
         Reserving `shut + tallest` (470 on a 390x844 phone) OVERSHOT the sheet's own cap (460), so
       the body had 83px more content than viewport and scrolled to a strip of nothing. Reserving to
       the cap EXACTLY is the version that satisfies both asks at once: the sheet is the same height
       as every other tab's, it does not move when a category opens, and the content ends precisely
       where the viewport does, so there is nothing to scroll to.
         ⚠ THE CAP IS READ, NOT RECOMPUTED. `getComputedStyle` resolves the `min(58%, 100% - 240px)`
       in shell.css to a used pixel value; duplicating that expression here would be a second copy of
       the sheet's sizing rule, in a different language, free to drift the day either term changes.
         ⚠ `grow <= 0` MEANS THE SHEET IS ALREADY AT THE CAP — with a tall category open, say. Leave
       the list alone rather than reserving a smaller number, which would fight the open body. */
    const panel = list.closest('.ds-side-panel');
    if (!panel) return;
    /* ⛔ THE CAP CANNOT BE READ OFF `maxHeight`, and getting that wrong is silent. shell.css sets
       `max-height: min(58%, 100% - 240px)` and `getComputedStyle` hands back that expression
       VERBATIM — percentages inside `min()` are not resolved at computed-value time, only at used
       value — so `parseFloat` returns NaN, the guard below treats it as "not laid out yet", and the
       reserve quietly never applies. Nothing throws; the sheet just keeps growing on open exactly as
       it did before the fix, which is precisely what Ryan reported still seeing.
         So the cap is MEASURED rather than parsed: overfill the list for one synchronous layout and
       read what the panel clamps itself to. That asks the browser the question instead of trying to
       re-evaluate its CSS, and it stays correct if the cap expression, the snap fraction or the
       reserved 3D band ever change. Both reads happen inside one frame with no paint between, so
       there is nothing to see. `scrollTop` is saved and restored because overfilling briefly makes
       the body scrollable and the browser can clamp it. */
    const keepScroll = list.closest('.ds-side-panel-body')?.scrollTop || 0;
    const naturalPanel = panel.offsetHeight;
    const naturalList = list.offsetHeight;
    list.style.minHeight = '100000px';
    const cap = panel.offsetHeight;
    list.style.minHeight = '';
    const bodyEl = list.closest('.ds-side-panel-body');
    if (bodyEl) bodyEl.scrollTop = keepScroll;
    // Not laid out yet (built detached, or the tab has never been shown). `refresh()` runs on every
    // tab entry, so the next one gets it — a zero here must not be written as a reserve.
    if (!cap || !naturalList) return;
    const grow = cap - naturalPanel;
    /* ⛔ AND THE LAST GOOD VALUE IS REMEMBERED, because `grow <= 0` is not "no reserve needed", it is
       "cannot measure one right now" — the sheet is already pinned at the cap by an open category,
       so there is no headroom to read. Returning there after the `minHeight = ''` above would leave
       the reserve CLEARED, and the sheet would then shrink the moment that category was closed:
       a pill toggle with a card open was enough to trigger it. Re-apply what was measured cleanly.
       (On a phone nothing auto-opens, so the first measurement on tab entry is always the clean one.) */
    /* ⛔ THE LARGEST CLEAN MEASUREMENT WINS, never simply the latest. The sheet's `max-height` rides
       a 0.55s transition, so a measurement taken while it is still rising reads the ANIMATING cap —
       341 instead of 460 on a 390x844 phone — and reserves about a third of the room. Every reading
       during the open is legitimate-looking and only the last one is right, so this keeps the high
       water mark rather than trusting whichever fired most recently. `reserved` is reset to 0 by the
       resize handler, which is the one event that can make a previous maximum wrong. */
    if (grow > 0) reserved = Math.max(reserved, Math.round(naturalList + grow));
    if (reserved) list.style.minHeight = `${reserved}px`;
  }

  /* ⛔ THE SHEET IS STILL COLLAPSED WHEN THE TAB REFRESHES, and that is why the reserve above did
     nothing at all for two rounds. `refresh()` runs as the step is mounted; the bottom sheet is
     still at its 48px header at that instant and only rises afterwards, so BOTH measurements — the
     natural height and the overfilled one — came back 48, `grow` was 0, and no reserve was written.
     Measured, not guessed: `window.__wdReserve` reported `{cap: 48, naturalPanel: 48}`.
       So the measurement waits for the sheet to have a real height, driven by the panel's own resize
     rather than by a timeout tuned to today's 0.55s open. One-shot: as soon as a reserve is computed
     the observer is dropped, which also stops the feedback loop — applying `min-height` resizes the
     panel, which would otherwise call this straight back. */
  let roomWatched = false;
  function watchForRoom() {
    if (roomWatched || !isPhone()) return;
    const panel = list.closest('.ds-side-panel');
    if (!panel) return;
    roomWatched = true;
    /* ⚠ THE SETTLE, NOT THE RESIZE. A ResizeObserver fires all the way UP the sheet's 0.55s slide,
       and every one of those frames offers a smaller cap than the real one — the first attempt at
       this locked in 341px of a 460px sheet. `transitionend` on `max-height` is the one moment the
       sheet is at its final height, so that is when the question gets asked. Left bound rather than
       one-shot: a later open/close re-measures for free, and the high-water rule above means a
       measurement taken while it is collapsing cannot make the reserve worse. */
    panel.addEventListener('transitionend', (e) => {
      if (e.target === panel && e.propertyName === 'max-height') reserveRoom();
    });
    /* ⛔ AND A BACKSTOP, because `prefers-reduced-motion` kills the transition outright — no
       transition, no `transitionend`, and the reserve would never be measured for exactly the users
       who are least served by a sheet that jumps around. */
    setTimeout(reserveRoom, 900);
  }

  /* The pills REWRAP when the width changes, so the tallest body is a different number after an
     orientation flip — and a stale reserve is either a slide coming back or a strip of dead space.
     Debounced because a rotate fires a burst, and this forces a synchronous layout to measure. */
  let reserveTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(reserveTimer);
    // ⚠ CLEAR THE HIGH WATER MARK FIRST — a rotate into a shorter viewport makes the previous
    // maximum too big, and `Math.max` would otherwise defend it forever.
    reserveTimer = setTimeout(() => { reserved = 0; list.style.minHeight = ''; reserveRoom(); }, 150);
  });

  function toggleCard(catId) {
    const c = cards.find((x) => x.cat.id === catId);
    if (!c.live) { ctx.announce?.(`${c.cat.label} are coming soon.`); return; }
    openCat = openCat === catId ? null : catId;
    syncAll(true);
    // Only on OPEN, and only from a real press — `refresh()` must not yank the pane around on tab
    // entry. Collapsing needs no scroll: nothing new appeared to be looked at.
    if (openCat === catId) revealCard(c);
    else scrollToken++; // cancel an in-flight reveal if this press closed the card instead
  }

  function togglePill(cat, kind) {
    const on = toggleProductSelection(ctx.state, cat.id, kind);
    ctx.announce?.(`${cat.label} ${kind} ${on ? 'selected' : 'removed'}.`);
    /* ⚠ MEASURED BEFORE THE REPAINT. `afterPickChange` re-renders the switcher, and on the 2→1
       crossing hides it — so the bar has to be measured while it still holds the thing being
       removed. See `tossAway`. */
    const from = on ? null : barPoint();
    afterPickChange();
    if (on) flyFrom(cat); else tossFrom(cat, from);
  }

  /** The switcher bar's counter, as a point, or null when there is no bar on screen. */
  function barPoint() {
    const t = ctx.switcherFlyTarget?.();
    if (!t) return null;
    const r = t.getBoundingClientRect();
    return r.width ? { cx: r.left + r.width / 2, cy: r.top + r.height / 2 } : null;
  }

  /** Throw this category's art off the screen, out of the switcher — the answer to `flyFrom`. */
  function tossFrom(cat, from) {
    const card = cards.find((c) => c.cat.id === cat.id);
    const img = card?.header.querySelector('.wd-pw-thumb img');
    tossAway(from, img?.currentSrc || img?.src);
  }

  /** Send a ghost of this category's tile to the switcher bar. Adds only — see `fly-to-switcher`. */
  function flyFrom(cat) {
    const card = cards.find((c) => c.cat.id === cat.id);
    /* ⚠ ON THE NEXT FRAME, because the bar may not exist yet. Crossing the 1→2 pick boundary is the
       moment `product-down-tab` un-hides itself, and that happens inside the `onChange` this
       function's caller just fired — so measuring the target synchronously here would read the rect
       of a still-hidden bar (all zeros) on the exact add the feature exists for. */
    requestAnimationFrame(() => {
      const target = ctx.switcherFlyTarget?.();
      if (target) flyToSwitcher(card?.header.querySelector('.wd-pw-thumb'), target);
    });
  }

  /** The one path out of any change to the picked set — a pill toggle or an added color. Shared so
   *  the adder cannot forget a step the pills take (the switcher hook, the repaint, the reserve). */
  function afterPickChange() {
    /* ⭐ BEFORE the switcher follows, so a configuration added here arrives already wearing its
       colour rather than flashing the stock plate first. By this point a logo has been uploaded
       (this tab is only reachable in-tool), so the palette exists and the sequence is real — the
       gate-time case is handled the other way round, in `logo-step` when the palette lands. */
    applyDefaultPlateColors(ctx.state);
    onChange?.(); // switcher + live product follow (main.js)
    syncAll(true);
    /* The reserve is measured from the tallest category body, and an adder appearing/disappearing
       moves that number. Without this the sheet keeps a reserve for a row that is no longer there
       (dead space) or lacks one for a row that just arrived (the slide this reserve exists to
       stop). Same reason `refresh()` re-runs it on every tab entry rather than once. */
    reserveRoom();
  }

  syncAll(false);

  return {
    el,
    // Open the category of the product you're currently viewing so its pills are right
    // there; else keep whatever was open. `animate` snaps on tab ENTRY (the panel was
    // hidden), but tweens when a top-bar switcher pick moves the live product while the
    // pane is already open — so the old header closes + the new one opens smoothly, as
    // if clicked by hand (Ryan 2026-07-16).
    refresh: ({ animate = false } = {}) => {
      const cur = ctx.state?.ui?.productCategory;
      // PHONE: NOTHING AUTO-OPENS (Ryan 2026-07-27: "as a default, make all 4 of the drop-downs
      // within that slide-up menu closed on mobile"). Auto-opening the live product's category is
      // right on a 320px-tall desktop pane, where the four headers and one open body all fit at
      // once; in a bottom sheet it spends most of the visible height on a category the buyer did not
      // ask for, and pushes the other three off-screen. Tapping a header still toggles it, and an
      // already-open one still survives a re-entry — only the automatic opening is dropped.
      // NOTHING PICKED → NOTHING AUTO-OPENS (Ryan 2026-08-02, the portal's rep boot: "all
      // categories closed"). The rule above is "open the category of the product you are currently
      // VIEWING" — with an empty picked set there is no such product, and the stage is blank.
      //   Stated rather than relied upon: `productCategory` happens to be null on that path today,
      // so all four are already shut by accident. That is one `DEFAULT_PRODUCT_KEY`-shaped change
      // away from silently opening a category the rep never chose, and the accident leaves no trace
      // for whoever makes it. This also covers the pre-existing deselect-everything case, where the
      // last-viewed category used to stay open over a blank stage.
      if (!isPhone() && cur && hasAnySelection(ctx.state) && cards.some((c) => c.cat.id === cur && c.live)) openCat = cur;
      syncAll(animate);
      /* AFTER `syncAll`, and on every entry rather than once. This is the first moment the list is
         reliably in the DOM and laid out at the sheet's real width (it is built detached), and the
         reserve depends on how the pills wrapped — which a product-set change can move. Running here
         also lands it BEFORE the shell measures its ease target: `setBody` defers that read one frame
         precisely because `refresh()` is allowed to change the content's height. */
      reserveRoom();
      /* …and again once the sheet actually has a height. On a phone this call is the one that
         matters — see `watchForRoom`: at this instant the sheet is still its 48px header, so the
         attempt above measures nothing usable. */
      watchForRoom();
    },
  };
}
